import { StatCard, StatGrid } from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import type { BOOTSTRAP_CONFIDENCE_INTERVAL_CALCULATOR_STRINGS } from "@/components/tools/bootstrap-confidence-interval-calculator";
import {
  Explanation,
  Formula,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import { HistogramPlot } from "@/components/tools/stats-descriptive-more/histogram-plot";
import { t } from "@/lib/i18n/interpolate";
import type { NumberFormatter } from "@/lib/tool-kit/stats-descriptive/format";
import type { BootstrapReport } from "@/lib/tool-kit/stats-descriptive-more/bootstrap";
import { histogramBins } from "@/lib/tool-kit/stats-descriptive-more/moments";

type Strings = typeof BOOTSTRAP_CONFIDENCE_INTERVAL_CALCULATOR_STRINGS;

export interface BootView {
  strings: Strings;
  fmt: NumberFormatter;
  num: (value: number) => string;
}

function between(lower: number, upper: number, view: BootView): string {
  return t(view.strings.rangeTemplate, {
    lower: view.num(lower),
    upper: view.num(upper),
  });
}

export function percentLabel(confidence: number, view: BootView): string {
  return view.fmt(confidence, { style: "percent", maximumFractionDigits: 1 });
}

export function statName(report: BootstrapReport, strings: Strings): string {
  const names = {
    mean: strings.statMean,
    median: strings.statMedian,
    sd: strings.statSd,
    correlation: strings.statCorrelation,
  };
  return names[report.statistic];
}

export function BootCards({
  report,
  view,
}: {
  report: BootstrapReport;
  view: BootView;
}) {
  const { strings, num } = view;
  const level = percentLabel(report.confidence, view);
  return (
    <StatGrid>
      <StatCard
        label={statName(report, strings)}
        value={num(report.estimate)}
      />
      <StatCard label={strings.cardSe} value={num(report.standardError)} />
      <StatCard label={strings.cardBias} value={num(report.bias)} />
      <StatCard
        emphasis
        label={t(strings.cardPercentile, { level })}
        value={between(report.percentile.lower, report.percentile.upper, view)}
      />
      <StatCard
        emphasis
        label={t(strings.cardBca, { level })}
        value={
          report.bca
            ? between(report.bca.lower, report.bca.upper, view)
            : strings.notAvailable
        }
      />
    </StatGrid>
  );
}

export function intervalRows(
  report: BootstrapReport,
  view: BootView
): string[][] {
  const { strings, num } = view;
  const row = (
    name: string,
    interval: { lower: number; upper: number } | null,
    note: string
  ) => [
    name,
    interval ? num(interval.lower) : strings.notAvailable,
    interval ? num(interval.upper) : strings.notAvailable,
    note,
  ];
  return [
    row(strings.methodPercentile, report.percentile, strings.notePercentile),
    row(
      strings.methodBca,
      report.bca,
      report.bca
        ? t(strings.noteBca, {
            z0: num(report.bca.z0),
            a: num(report.bca.acceleration),
          })
        : strings.noteBcaFailed
    ),
    row(strings.methodBasic, report.basic, strings.noteBasic),
    row(strings.methodNormal, report.normal, strings.noteNormal),
  ];
}

export function BootPlot({
  report,
  view,
}: {
  report: BootstrapReport;
  view: BootView;
}) {
  const { strings, fmt, num } = view;
  const k = Math.min(
    60,
    Math.max(10, Math.round(Math.sqrt(report.replicates.length) / 2))
  );
  const preferred = report.bca ?? report.percentile;
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.plotTitle}</h3>
      <HistogramPlot
        bins={histogramBins(report.replicates, k)}
        fmt={fmt}
        label={t(strings.plotLabel, {
          count: fmt(report.replicates.length),
          estimate: num(report.estimate),
          lower: num(preferred.lower),
          upper: num(preferred.upper),
        })}
        markers={[
          { value: report.estimate, label: strings.markEstimate },
          { value: report.percentile.lower, label: "", dashed: true },
          { value: report.percentile.upper, label: "", dashed: true },
          ...(report.bca
            ? [
                { value: report.bca.lower, label: strings.markBca },
                { value: report.bca.upper, label: strings.markBca },
              ]
            : []),
        ]}
        yLabel={strings.plotY}
      />
      <p className="text-muted-foreground text-xs">{strings.plotNote}</p>
    </div>
  );
}

export function BootWorking({
  report,
  view,
}: {
  report: BootstrapReport;
  view: BootView;
}) {
  const { strings, fmt, num } = view;
  const bca = report.bca;
  const count = report.replicates.length;
  const below = report.replicates.filter(
    (value) => value < report.estimate
  ).length;
  return (
    <WorkingPanel title={strings.workingTitle}>
      <Formula>
        {t(strings.workSetup, {
          n: fmt(report.n),
          resamples: fmt(report.resamples),
          seed: fmt(report.seed, { useGrouping: false }),
        })}
      </Formula>
      <Formula>
        {t(strings.workSe, {
          se: num(report.standardError),
          bias: num(report.bias),
        })}
      </Formula>
      <Formula>
        {t(strings.workPercentile, {
          alpha: num((1 - report.confidence) / 2),
          rank: num(((count + 1) * (1 - report.confidence)) / 2),
          lower: num(report.percentile.lower),
          upper: num(report.percentile.upper),
        })}
      </Formula>
      {bca ? (
        <>
          <Formula>
            {t(strings.workZ0, {
              below: fmt(below),
              count: fmt(count),
              z0: num(bca.z0),
            })}
          </Formula>
          <Formula>{t(strings.workA, { a: num(bca.acceleration) })}</Formula>
          <Formula>
            {t(strings.workAdjusted, {
              low: num(bca.adjustedLower),
              high: num(bca.adjustedUpper),
              lower: num(bca.lower),
              upper: num(bca.upper),
            })}
          </Formula>
        </>
      ) : null}
      {report.dropped > 0 ? (
        <Explanation>
          {t(strings.workDropped, { count: fmt(report.dropped) })}
        </Explanation>
      ) : null}
      <Explanation>{strings.workRng}</Explanation>
    </WorkingPanel>
  );
}
